import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { gray700 } from "core/resources/theme/colors";
export type SCurvePoint = {
  semana: string;
  data: string;
  planejado: number | null;
  realizado: number | null;
  projecao: number | null;
};

type Props = {
  data?: SCurvePoint[] | null;
  loading?: boolean;
};

function RdoSCurveChart({ data, loading }: Props) {

  if (loading) {
    return <Empty>Carregando curva S…</Empty>;
  }

  if (!data?.length) {
    return <Empty>Sem dados de curva S</Empty>;
  }

  const primeiroIndiceRealizado = data.findIndex(
    (item) => item.realizado != null
  );
  
  const dataComInicioZero =
    primeiroIndiceRealizado >= 0
      ? (() => {
          const primeiroPonto = data[primeiroIndiceRealizado];
  
          const diaAnterior = new Date(primeiroPonto.data);
          diaAnterior.setDate(diaAnterior.getDate() - 1);
  
          return [
            {
              ...primeiroPonto,
              data: diaAnterior.toISOString().split("T")[0],
              semana: "",
              realizado: 0,
              planejado: 0,
              projecao: null,
            },
            ...data,
          ];
        })()
      : data;
  
  const ultimoIndiceRealizado = dataComInicioZero.reduce(
    (ultimo, item, index) =>
      item.realizado != null ? index : ultimo,
    -1
  );
  
  const dataNormalizada = (() => {
    let ultimoRealizado = 0;
    let ultimoPlanejado: number | null = null;
  
    return dataComInicioZero.map((item, index) => {
      if (item.realizado != null) {
        ultimoRealizado = item.realizado;
      }
  
      if (item.planejado != null) {
        ultimoPlanejado = item.planejado;
      }
  
      return {
        ...item,
        realizado:
          index > ultimoIndiceRealizado
            ? null
            : item.realizado ?? ultimoRealizado,
  
        planejado:
          item.planejado ?? ultimoPlanejado,
      };
    });
  })();

  const hasPlanejado = dataNormalizada.some((d) => d.planejado != null);
  const hasRealizado = dataNormalizada.some((d) => d.realizado != null);

  let realIndex = -1;
  let planIndex = -1;

  for (let i = dataNormalizada.length - 1; i >= 0; i--) {
    if (realIndex === -1 && dataNormalizada[i].realizado != null) {
      realIndex = i;
    }

    if (planIndex === -1 && dataNormalizada[i].planejado != null) {
      planIndex = i;
    }

    if (realIndex !== -1 && planIndex !== -1) {
      break;
    }
  }

  const hasProjecao =
    realIndex >= 0 &&
    planIndex >= 0 &&
    planIndex > realIndex;

  const valorInicial = dataNormalizada[realIndex]?.realizado;
  const valorFinal = dataNormalizada[planIndex]?.planejado;

  const dataComProjecao = dataNormalizada.map(
    (item, index) => {
      let projecaoCalculada: number | null = null;
  
      if (
        hasProjecao &&
        valorInicial != null &&
        valorFinal != null &&
        index >= realIndex &&
        index <= planIndex
      ) {
        const percentual =
          (index - realIndex) /
          (planIndex - realIndex);
  
        projecaoCalculada =
          valorInicial +
          (valorFinal - valorInicial) * percentual;
      }
  
      return {
        ...item,
        projecaoCalculada: projecaoCalculada?.toFixed(2),
      };
    }
  );

  const hoje = new Date().toISOString().split("T")[0];

  const pontoHoje = dataNormalizada.find(
    (d) => d.data === hoje
  );
  const semanaCorte = pontoHoje?.semana;

  const yFinal =
  planIndex >= 0
    ? (dataNormalizada[planIndex].planejado ?? 100)
    : 100;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
  data={dataComProjecao}
  margin={{ top: 8, right: 20, left: -20, bottom: 6 }}
      >
        <XAxis dataKey="semana" tick={false} />

        <YAxis
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          axisLine={false}
          tickLine={false}
          tick={false}
        />

        <ReferenceLine
          y={0}
          stroke="#d1d5db"
          strokeDasharray="4 4"
          label={{
            value: "0%",
            position: "left",
            fill: "#6B7280",
            offset: 8,
            fontSize: 9,
          }}
        />

        <ReferenceLine
          y={50}
          stroke="#E5E7EB"
          strokeDasharray="4 4"
          label={{
            value: "50%",
            position: "left",
            fill: "#6B7280",
            offset: 8,
            fontSize: 9,
          }}
        />

        <ReferenceLine
          y={yFinal}
          stroke="#d1d5db"
          strokeDasharray="4 4"
          label={{
            value: `${yFinal}%`,
            position: "left",
            fill: "#6B7280",
            offset: 8,
            fontSize: 9,
          }}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) {
              return null;
            }

            const payloadFiltrado = payload.filter((item) => {
              return !(
                item.name === "Projeção" &&
                item.payload?.data ===
                  dataNormalizada[realIndex].data
              );
            });
            return (
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #ccc",
                  padding: "8px",
                }}
              >
                <p>{label}</p>

                {payloadFiltrado.map((item) => (
                  <p
                    key={String(item.name)}
                    style={{ color: item.color }}
                  >
                    {item.name}: {item.value}%
                  </p>
                ))}
              </div>
            );
          }}
        />

        {hasProjecao && (
          <Line
            type="linear"
            dataKey="projecaoCalculada"
            name="Projeção"
            stroke="#f97316"
            strokeWidth={3}
            strokeDasharray="10 6"
            connectNulls
            dot={false}
          />
        )}

        {semanaCorte && (
          <ReferenceLine
            x={semanaCorte}
            stroke="#6b7280"
            strokeWidth={2}
            strokeDasharray="6 6"
            label={{
              value: "Hoje",
              position: "bottom",
            }}
          />
        )}

        {hasRealizado && (
          <Line
            type="monotone"
            dataKey="realizado"
            name="Realizado"
            stroke="#16a34a"
            strokeWidth={3}
            dot={{ r: 0 }}
            connectNulls={false}
          />
        )}

        {hasPlanejado && (
          <Line
            type="monotone"
            dataKey="planejado"
            name="Planejado"
            stroke="#2563eb"
            strokeWidth={3}
            dot={{ r: 0 }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default RdoSCurveChart;

const Empty = styled.div`
  flex: 1;
  display: grid;
  place-items: center;
  color: ${gray700};
  font-size: 0.8rem;
`;